import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showApiErrorToast, showSuccessToast } from '@utils'
import { useLanguage } from '@context'

export type PinKind = 'hashtag' | 'category'

interface SyncProfilePinsData {
  // The id the profile screen was opened with: only used to refresh it.
  profileId: string
  accountId: string
  previousIds: string[]
  nextIds: string[]
}

const fetchContactAccountIds = async (kind: PinKind, id: string): Promise<string[]> => {
  if (kind === 'hashtag') {
    const { data } = await api.get<{ hashtag: { contactAccountIds?: string[] } }>(
      `/hashtags/${id}`,
    )
    return data.hashtag.contactAccountIds ?? []
  }
  const { data } = await api.get<{ category: { contactAccountIds?: string[] } }>(
    `/categories/${id}`,
  )
  return data.category.contactAccountIds ?? []
}

const updateContactAccountIds = async (
  kind: PinKind,
  id: string,
  contactAccountIds: string[],
): Promise<void> => {
  if (kind === 'hashtag') {
    await api.put(`/hashtag/${id}`, { contactAccountIds })
    return
  }
  // Same body as useUpdateCategory: the route only reads multipart.
  const formData = new FormData()
  if (contactAccountIds.length > 0) {
    contactAccountIds.forEach((contactId) => {
      formData.append('contactAccountIds[]', contactId)
    })
  } else {
    formData.append('contactAccountIds', '[]')
  }
  await api.put(`/category/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
}

// A member is put in a hashtag or a category through the label's own list of
// people, as Menu > Hashtags / Catégories does: each label that changed is
// read, then written back with the person added or removed.
export const syncProfilePins = async (
  kind: PinKind,
  { accountId, previousIds, nextIds }: Pick<SyncProfilePinsData, 'accountId' | 'previousIds' | 'nextIds'>,
): Promise<void> => {
  const toRemove = previousIds.filter((id) => !nextIds.includes(id))
  const toAdd = nextIds.filter((id) => !previousIds.includes(id))

  const results = await Promise.allSettled([
    ...toRemove.map(async (id) => {
      const current = await fetchContactAccountIds(kind, id)
      await updateContactAccountIds(
        kind,
        id,
        current.filter((x) => x !== accountId),
      )
    }),
    ...toAdd.map(async (id) => {
      const current = await fetchContactAccountIds(kind, id)
      await updateContactAccountIds(
        kind,
        id,
        current.includes(accountId) ? current : [...current, accountId],
      )
    }),
  ])

  // Every label is tried, but one failure is reported: no false success.
  const failed = results.find((result) => result.status === 'rejected')
  if (failed) throw (failed as PromiseRejectedResult).reason
}

export const useSyncProfilePins = (kind: PinKind) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()

  return useMutation<void, Error, SyncProfilePinsData>({
    mutationFn: (variables) => syncProfilePins(kind, variables),
    // Two quick saves run one after the other: a read-then-write of the same
    // label cannot cross another one.
    scope: { id: `profile-pins-${kind}` },
    onSuccess: () => {
      showSuccessToast(
        t('toast.success'),
        t(kind === 'hashtag' ? 'toast.hashtagUpdated' : 'toast.categoryUpdated'),
      )
    },
    onError: (error) => showApiErrorToast(t, error),
    // Also after a failure: the picker must show what is really saved.
    onSettled: async (_data, _error, variables) => {
      const allIds = [...new Set([...variables.previousIds, ...variables.nextIds])]
      const list = kind === 'hashtag' ? 'hashtags' : 'categories'
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['profile', variables.profileId] }),
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: [list] }),
        ...allIds.map((id) =>
          Promise.all([
            queryClient.invalidateQueries({ queryKey: [kind, id] }),
            queryClient.invalidateQueries({ queryKey: [`${kind}-contacts`, { id }] }),
          ]),
        ),
      ])
    },
  })
}
